/**
 * Кабинет подрядчика «Мой график» (ПЛ-32): работы графиков, где подрядчик — исполнитель; план, основной
 * базовый план и факт без стоимости. Факт подрядчик отмечает отчётом о выполнении — генподрядчик принимает его
 * в график или отклоняет с причиной.
 */
import type { ContractorActivity, ContractorSchedule } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { LField } from './LField';

const dd = (s: string | null) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '—');
const n1 = (x: number | null) => (x == null ? '—' : String(Math.round(x * 10) / 10).replace('.', ','));
const num = (s: string) => {
  const v = Number(s.replace(/\s/g, '').replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(v) ? null : v;
};
const localDay = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const STATUS = { not_started: 'не начата', in_progress: 'идёт', done: 'выполнена' } as const;

export function ContractorSchedulePage() {
  useCrumbs([{ label: 'Мой график' }]);
  const project = (() => {
    try {
      return new URLSearchParams(window.location.search).get('project') ?? undefined;
    } catch {
      return undefined;
    }
  })();
  const q = useQuery({
    queryKey: ['contractor-schedule', project],
    queryFn: () => api<ContractorSchedule>('/contractor/schedule', { query: { projectId: project } }),
  });
  const [edit, setEdit] = useState<ContractorActivity | null>(null);
  return (
    <Page>
      {project ? (
        <p className="mb-3 text-[14px]">
          Показан один проект.{' '}
          <a
            href="/schedule/contractor"
            className="inline-flex min-h-11 items-center text-accent-text hover:underline md:min-h-0"
          >
            Все проекты
          </a>
        </p>
      ) : null}
      <PageHeader
        title="Мой график"
        subtitle="Работы, где ваша организация — исполнитель: план, базовый план и факт. Отметьте выполнение — генподрядчик проверит и примет в график."
      />
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data && !q.data.projects.length ? (
        <EmptyState title="Работ пока нет">
          Генподрядчик ещё не назначил вашей организации работ в графиках проектов, куда вас пригласили.
        </EmptyState>
      ) : null}
      <div className="flex flex-col gap-4">
        {(q.data?.projects ?? []).map((p) => (
          <Panel
            key={p.scheduleId}
            title={`${p.projectCode} · ${p.projectName}`}
            meta={`${p.activities.length} работ${p.dataDate ? ` · дата отчёта ${dd(p.dataDate)}` : ''}${p.baselineName ? ` · базовый план «${p.baselineName}»` : ''}`}
          >
            <ul className="divide-y divide-line text-[14px]">
              {p.activities.map((a) => (
                <li key={a.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {a.code} {a.name}
                    </div>
                    {a.wbsPath ? <div className="text-[12px] text-ink-2">{a.wbsPath}</div> : null}
                    <div className="text-[12px] text-ink-2">
                      план {dd(a.start)} — {dd(a.finish)}
                      {a.blFinish ? ` · по базовому до ${dd(a.blFinish)}` : ''}
                      {a.varianceDays ? (
                        <span className={clsx(a.varianceDays > 0 && 'text-danger')}>
                          {' '}
                          ({a.varianceDays > 0 ? `+${a.varianceDays}` : a.varianceDays} дн)
                        </span>
                      ) : null}
                    </div>
                    <div className="text-[12px]">
                      {STATUS[a.status]}
                      {a.actualStart ? ` · начата ${dd(a.actualStart)}` : ''}
                      {a.actualFinish ? ` · окончена ${dd(a.actualFinish)}` : ''}
                      {a.status === 'in_progress' ? ` · ${n1(a.percent)} %` : ''}
                      {a.quantity != null
                        ? ` · объём ${n1(a.actualQuantity ?? 0)} из ${n1(a.quantity)} ${a.unit ?? ''}`
                        : ''}
                    </div>
                    {a.pending ? (
                      <div className="text-[12px] text-ink-2">
                        Отчёт от {dd(a.pending.at)} ждёт проверки генподрядчика
                      </div>
                    ) : a.last?.status === 'rejected' ? (
                      <div className="text-[12px] text-danger">
                        Отчёт отклонён{a.last.note ? `: ${a.last.note}` : ''}
                      </div>
                    ) : a.last?.status === 'accepted' ? (
                      <div className="text-[12px] text-success">Последний отчёт принят {dd(a.last.at)}</div>
                    ) : null}
                  </div>
                  <span className="flex items-center gap-2">
                    {a.pending ? <Badge tone="info">на проверке</Badge> : null}
                    {a.status !== 'done' ? (
                      <Button size="sm" onClick={() => setEdit(a)} className="max-md:h-11">
                        Отметить выполнение
                      </Button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        ))}
      </div>
      {edit ? <UpdateDialog a={edit} onClose={() => setEdit(null)} /> : null}
    </Page>
  );
}

function UpdateDialog({ a, onClose }: { a: ContractorActivity; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    start: a.actualStart?.slice(0, 10) ?? '',
    finish: '',
    percent: a.hasSteps ? '' : String(a.percent || ''),
    remaining: a.remainingDays != null ? String(a.remainingDays) : '',
    quantity: a.actualQuantity != null ? String(a.actualQuantity) : '',
    comment: '',
  });
  const save = useMutation({
    mutationFn: () =>
      api(`/contractor/activities/${a.id}/updates`, {
        body: {
          // Начало уже в графике и не менялось — не отправляется: время, поставленное в графике, не затирается
          actualStart: f.start && f.start !== a.actualStart?.slice(0, 10) ? `${f.start}T08:00` : null,
          actualFinish: f.finish ? `${f.finish}T17:00` : null,
          percent: a.hasSteps || f.finish ? null : num(f.percent),
          remainingDays: f.finish ? null : num(f.remaining),
          actualQuantity: num(f.quantity),
          comment: f.comment || null,
        },
      }),
    onSuccess: () => {
      toast('Отчёт отправлен генподрядчику');
      void qc.invalidateQueries({ queryKey: ['contractor-schedule'] });
      onClose();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const max = localDay();
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${a.code} ${a.name}`}
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => save.mutate()}
            className="max-md:h-11"
          >
            Отправить отчёт
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <LField label="Фактическое начало">
          <Input
            type="date"
            max={max}
            value={f.start}
            onChange={(e) => setF({ ...f, start: e.target.value })}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Фактическое окончание" hint="Если работа закончена">
          <Input
            type="date"
            max={max}
            value={f.finish}
            onChange={(e) => setF({ ...f, finish: e.target.value })}
            className="max-md:h-11"
          />
        </LField>
        {!a.hasSteps && !f.finish ? (
          <LField label="Выполнено, %">
            <Input
              inputMode="decimal"
              value={f.percent}
              onChange={(e) => setF({ ...f, percent: e.target.value })}
              className="max-md:h-11"
            />
          </LField>
        ) : null}
        {!f.finish ? (
          <LField label="Осталось, рабочих дней">
            <Input
              inputMode="decimal"
              value={f.remaining}
              onChange={(e) => setF({ ...f, remaining: e.target.value })}
              className="max-md:h-11"
            />
          </LField>
        ) : null}
        {a.quantity != null ? (
          <LField label={`Выполненный объём, ${a.unit ?? ''}`}>
            <Input
              inputMode="decimal"
              value={f.quantity}
              onChange={(e) => setF({ ...f, quantity: e.target.value })}
              className="max-md:h-11"
            />
          </LField>
        ) : null}
        <LField label="Комментарий">
          <Input
            value={f.comment}
            onChange={(e) => setF({ ...f, comment: e.target.value })}
            className="max-md:h-11"
          />
        </LField>
      </div>
      {a.hasSteps ? (
        <p className="mt-2 text-[12px] text-ink-2">
          Процент этой работы считается по шагам выполнения — его отмечает генподрядчик.
        </p>
      ) : null}
    </Dialog>
  );
}
